/**
 * The correlation matrix as an APA-style table, and as CSV.
 *
 * The APA 7 convention for correlation tables lays variables down the side and their
 * numbers across the top, prints only the lower triangle with a dash on the diagonal,
 * drops the leading zero from r (it cannot exceed 1), adds M and SD columns, and marks
 * significance with asterisks defined in a table note. The text here is what is shown on
 * the page and what the copy button puts on the clipboard.
 */

import {
  machineNumber,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type MatrixReport,
  stars,
} from "@/lib/tool-kit/stats-regression/correlation-matrix";
import { apaBounded } from "@/lib/tool-kit/stats-research/format";

export interface ApaTable {
  head: string[];
  rows: string[][];
}

const DASH = "—";
const CSV_SPECIAL = /[",\n]/;

/** One cell: r with its stars, or a dash for an undefined pair. */
export function apaCell(
  fmt: NumberFormatter,
  r: number,
  p: number,
  decimals: number
): string {
  if (Number.isNaN(r)) {
    return DASH;
  }
  return `${apaBounded(fmt, r, decimals)}${stars(p)}`;
}

export function apaTable(
  report: MatrixReport,
  names: readonly string[],
  fmt: NumberFormatter,
  decimals: number,
  labels: { variable: string; mean: string; sd: string }
): ApaTable {
  const numbers = names.map((_, i) => String(i + 1));
  const head = [labels.variable, labels.mean, labels.sd, ...numbers];
  const rows = names.map((name, i) => {
    const cells = names.map((_, j) => {
      if (j > i) {
        return "";
      }
      return i === j
        ? DASH
        : apaCell(fmt, report.r[i][j], report.pAdjusted[i][j], decimals);
    });
    return [
      `${i + 1}. ${name}`,
      fmt(report.means[i], { maximumFractionDigits: decimals }),
      fmt(report.sds[i], { maximumFractionDigits: decimals }),
      ...cells,
    ];
  });
  return { head, rows };
}

/** Tab-separated text, ready to paste into a Word or Sheets table. */
export function toTsv(table: ApaTable): string {
  return [table.head, ...table.rows].map((row) => row.join("\t")).join("\n");
}

function csvCell(value: string): string {
  return CSV_SPECIAL.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** The full symmetric matrix of r as CSV with ASCII digits (for spreadsheets). */
export function matrixCsv(
  values: readonly (readonly number[])[],
  names: readonly string[]
): string {
  const lines = [
    ["", ...names].map(csvCell).join(","),
    ...values.map((row, i) =>
      [csvCell(names[i]), ...row.map((value) => machineNumber(value, 6))].join(
        ","
      )
    ),
  ];
  return lines.join("\n");
}
